import React, { useEffect, useState, useMemo } from "react"; // Import useMemo
import "../../../css/SlotTempletTable.css";
import SlotTemplateModal from "./SlotTemplateModal";
import ViewSlotTemplateModal from "../../../components/Super Admin/Core Functions/ViewSlotTemplateModal";
import api from "../../../api/axiosConfig";

interface Slot {
  start_time: string;
  end_time: string;
  slot_type: string;
}

interface SlotTemplate {
  _id: string;
  name: string;
  created_on: string;
  disabled_on?: string;
  slots?: Slot[];
}

const SlotTemplateTable: React.FC = () => {
  const [slotTemplates, setSlotTemplates] = useState<SlotTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SlotTemplate | null>(
    null
  );

  // 🔽 1. Add state for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // You can change this value

  /**
   * Fetches all slot templates and their detailed slot information from the API.
   */
  const fetchSlotTemplates = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        console.error("Authentication token not found.");
        setLoading(false);
        return;
      }

      // Step 1: Get the list of all template stubs
      const res = await api.appointment_api.get("/api/slot-templates", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const templates: SlotTemplate[] = res.data;

      // Step 2: Fetch the full details (including slots) for each template
      const templatesWithSlots = await Promise.all(
        templates.map(async (template) => {
          try {
            const detailRes = await api.appointment_api.get(
              `/api/slot-templates/${template._id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            return { ...template, slots: detailRes.data.slots || [] };
          } catch (err) {
            console.error(`Error fetching slots for template ${template._id}`, err);
            return { ...template, slots: [] };
          }
        })
      );

      setSlotTemplates(templatesWithSlots);
    } catch (error) {
      console.error("Error fetching slot templates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlotTemplates();
  }, []);

  // 🔽 2. Calculate pagination variables
  const totalPages = Math.ceil(slotTemplates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Use useMemo to slice the templates only when data or page changes
  const currentTemplatesToDisplay = useMemo(() => {
    return slotTemplates.slice(startIndex, endIndex);
  }, [slotTemplates, startIndex, endIndex]);


  const handleRowClick = (template: SlotTemplate) => {
    setSelectedTemplate(template);
  };

  const handleTemplateAdded = () => {
    setIsModalOpen(false);
    fetchSlotTemplates();
    setCurrentPage(1); // Reset to first page after adding
  };

  return (
    <div className="slot-template-card">
      <div className="slot-template-header">
        <h2>Slot Templates</h2>
        <button className="add-slot-btn" onClick={() => setIsModalOpen(true)}>
          + Add New Slot
        </button>
      </div>

      {loading ? (
        <p>Loading slot templates...</p>
      ) : (
        <table className="slot-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Slot Name</th>
              <th>Number of Slots</th>
              <th>Status</th>
              <th>Created On</th>
              <th>Disabled On</th>
            </tr>
          </thead>
          <tbody>
            {/* 🔽 3. Map over the paginated data */}
            {currentTemplatesToDisplay.length > 0 ? (
              currentTemplatesToDisplay.map((row, index) => (
                <tr
                  key={row._id}
                  onClick={() => handleRowClick(row)}
                  style={{ cursor: "pointer" }}
                >
                  {/* 🔽 4. Update index to be correct across pages */}
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>{row.slots?.length || 0}</td>
                  <td>
                    <span
                      className={`badge status ${
                        row.disabled_on ? "inactive" : "active"
                      }`}
                    >
                      {row.disabled_on ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td>{new Date(row.created_on).toLocaleDateString()}</td>
                  <td>
                    {row.disabled_on
                      ? new Date(row.disabled_on).toLocaleDateString()
                      : "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No slot templates found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Modal for adding a new template */}
      <SlotTemplateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTemplateAdded={handleTemplateAdded}
      />

      {/* Modal for viewing and triggering the edit of an existing template */}
      {selectedTemplate && (
        <ViewSlotTemplateModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onUpdate={fetchSlotTemplates} // Pass the refresh function
        />
      )}

      {/* 🔽 5. Dynamic Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(pageNumber => (
            <button
              key={pageNumber}
              onClick={() => setCurrentPage(pageNumber)}
              className={currentPage === pageNumber ? "active" : ""}
            >
              {pageNumber}
            </button>
          ))}
          
          <button
            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SlotTemplateTable;